import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { FormActions } from "../../components/common/FormActions";
import {
  API_QR_CODE_MERCHANT_SERVICES,
  API_GET_CONFIGURABLE_MERCHANTS_ID,
} from "../../utils/apis";
import {
  createEditAction,
  createViewAction,
  formatCurrency,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_QR_CODE_MERCHANT_SERVICES } from "../../utils/urls";
import { IFormSection, TFormHookParams } from "../../types/common";
import useGet from "../common/useGet";
import { MODAL } from "../../utils/constants";

const url = URL_QR_CODE_MERCHANT_SERVICES;

export const useQRcodeMerchantServices = () => {
  const tableColumns = () => [
    { id: "merchant_name", header: "Merchant", filter: {} },
    { id: "service_name", header: "Service Name", filter: {} },
    { id: "is_nested", header: "Nested Menu", filter: {} },
    { id: "service_cost", header: "Service Cost", filter: {} },
    { id: "is_fixed", header: "Adjustable Cost", filter: {} },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any) =>
    data?.map((item: any) => ({
      ...item,
      merchant_name: item?.merchant_name,
      is_nested: item?.is_nested ? "Yes" : "No",
      is_fixed: item?.is_fixed ? "Yes" : "No",
      service_cost: item?.is_nested ? "—" : item?.service_cost,
      actions: (
        <FormActions
          actions={[
            createViewAction(url, item?.id),
            createEditAction(url, item?.id),
          ]}
        />
      ),
    })) ?? [];

  const viewData = (item: any) => {
    const data = item?.results || item;
    const nestedServices: any[] = data?.nested_services_details || [];

    return {
      sections: [
        {
          title: "QR Code Merchant Service Details",
          fields: {
            merchant:
              data?.merchant_name ||
              data?.merchant?.business_name ||
              data?.merchant?.username ||
              data?.merchant,
            service_name: data?.service_name,
            service_code: data?.service_code,
            is_nested: data?.is_nested ? "Yes" : "No",
            service_cost: data?.is_nested ? "—" : data?.service_cost,
            is_fixed: data?.is_fixed ? "Yes" : "No",
          },
        },
        ...(data?.is_nested && nestedServices.length > 0
          ? [
              {
                title: "Nested Services",
                // one row per nested service: name -> cost
                // (index prefix keeps keys unique if two names match)
                fields: nestedServices.reduce(
                  (acc: Record<string, any>, s: any, idx: number) => ({
                    ...acc,
                    [`${idx + 1}. ${s?.service_name}`]: formatCurrency(
                      s?.service_cost,
                    ),
                  }),
                  {},
                ),
              },
            ]
          : []),
      ],
    };
  };

  const formRoute = getForm(url);
  const tableTitle = "QR Code Merchant Services";

  return { tableData, tableColumns, formRoute, viewData, tableTitle };
};

export const useQRcodeMerchantServicesForm = ({ form }: TFormHookParams) => {
  const {
    data: merchantIDs,
    loading: merchantIDsLoading,
    loadData: merchantIDsLoadData,
  } = useGet();

  useEffect(() => {
    merchantIDsLoadData({ api: API_GET_CONFIGURABLE_MERCHANTS_ID });
  }, []);

  const routeParams = useParams();
  const recordId = routeParams.id ?? Object.values(routeParams)[0];
  const { data: savedService, loadData: loadSavedService } = useGet();

  useEffect(() => {
    if (recordId) {
      loadSavedService({ api: `${API_QR_CODE_MERCHANT_SERVICES}${recordId}/` });
    }
  }, [recordId]);

  const originalNested: any[] =
    (savedService?.results || savedService)?.nested_services_details || [];

  const attachNestedIds = (rows: any[]) => {
    const used = new Set<string>();

    const byName = rows.map((r) => {
      if (r.id) {
        used.add(r.id);
        return r.id;
      }
      const match = originalNested.find(
        (o) => !used.has(o.id) && o.service_name === r.service_name,
      );
      if (match) used.add(match.id);
      return match?.id;
    });

    return rows.map((r, i) => {
      let id = byName[i];
      if (!id) {
        const positional = originalNested[i];
        if (positional && !used.has(positional.id)) {
          used.add(positional.id);
          id = positional.id;
        }
      }
      return { ...r, ...(id && { id }) };
    });
  };

  const booleanOptions = [
    { id: false, name: "No" },
    { id: true, name: "Yes" },
  ];

  const merchantOptions: { id: any; name: any }[] =
    merchantIDs?.map((m: any) => ({ id: m?.id, name: m?.name })) ?? [];

  const selectedMerchantId =
    form?.merchant && typeof form.merchant === "object"
      ? form.merchant.id
      : form?.merchant;

  if (
    selectedMerchantId &&
    !merchantOptions.some((o) => o.id === selectedMerchantId)
  ) {
    merchantOptions.push({
      id: selectedMerchantId,
      name: form?.merchant_name || selectedMerchantId,
    });
  }

  const nestedServiceFields = [
    {
      field_name: "service_name",
      type: "text",
      label: "Service Name",
      props: {
        required: true,
        placeholder: "Enter Service Name",
      },
    },
    {
      field_name: "service_cost",
      type: "number",
      label: "Service Cost",
      props: {
        required: true,
        placeholder: "Enter Service Cost",
        min: 0,
      },
    },
    {
      field_name: "is_fixed",
      type: "select",
      label: "Adjustable Service Cost",
      props: {
        required: true,
        value: form?.data?.is_fixed,
        placeholder: "Select Option",
      },
      options_data: booleanOptions,
    },
  ];

  const sections: IFormSection[] = [
    {
      title: "QR Code Merchant Service Details",
      fields: [
        {
          field_name: "merchant",
          type: "select",
          label: "Merchant",
          loading: merchantIDsLoading,
          props: {
            required: true,
            value: form?.merchant,
            placeholder: "Select Merchant",
          },
          options_data: merchantOptions,
        },
        {
          field_name: "service_name",
          type: "text",
          label: "Service Name",
          props: {
            required: true,
            placeholder: "Enter Service Name",
          },
        },
        {
          field_name: "is_nested",
          type: "select",
          label: "Has Nested Menu Items",
          description:
            "Select Yes if this service contains nested menu items. Nested services do not have a service cost.",
          props: {
            required: true,
            value: form?.is_nested,
            placeholder: "Select Option",
          },
          options_data: booleanOptions,
        },
        ...(form?.is_nested
          ? [
              {
                field_name: "nested_services_details",
                type: MODAL,
                label: "Add Nested Services",
                fields: nestedServiceFields,
                props: {
                  multiple: true,
                  wide: true,
                  display: "table",
                },
              },
            ]
          : [
              {
                field_name: "service_cost",
                type: "number",
                label: "Service Cost",
                props: {
                  required: true,
                  placeholder: "Enter Service Cost",
                  min: 0,
                },
              },
            ]),
        {
          field_name: "is_fixed",
          type: "select",
          label: "Adjustable Service Cost",
          description:
            "Select Yes if the service amount can be adjusted by the user.",
          props: {
            required: true,
            value: form?.is_fixed,
            placeholder: "Select Option",
          },
          options_data: booleanOptions,
        },
      ],
    },
  ];

  const redirect = getRegister(url);

  function onSubmit(payload: any, id: string | number) {
    const { nested_services_details, nested_services, ...rest } = payload;

    const params: any = {
      ...rest,
      merchant:
        typeof payload.merchant === "object"
          ? payload.merchant.id
          : payload.merchant,
    };

    if (params.is_nested) {
      // nested services carry their own cost; the parent has none
      delete params.service_cost;
      params.nested_services = attachNestedIds(
        nested_services_details || nested_services || [],
      ).map((s: any) => ({
        // existing nested services carry their id; new ones have none
        ...(s.id && { id: s.id }),
        service_name: s.service_name,
        service_cost: Number(s.service_cost),
        is_fixed: s.is_fixed,
      }));
    }

    return {
      id,
      api: API_QR_CODE_MERCHANT_SERVICES,
      params,
    };
  }

  return { sections, redirect, onSubmit };
};
